import { message } from 'antd';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import IUserRepository from '../../2_domain/user/iUserRepository';
import User, {
  PasswordInfo,
  ResetInfo,
  SignInInfo,
  UserLoginState,
} from '../../2_domain/user/user';
import Connection from '../../3_infrastructure/core/connection';
import Get from '../../lib/di/get';
import promiseResolver from '../../lib/util/promiseResolver';
import userAtom from './userAtom';
import {
  showSuccess,
  showError,
} from '../../0_presentation/core/components/feedback/snackbar';
import { useTheme } from 'styled-components';

const useUser = () => {
  const [user, setUser] = useRecoilState(userAtom);
  const [isLoading, setIsLoading] = useState(false);
  const userRepo = Get.find<IUserRepository>('IUserRepository');
  const connection = Get.find<Connection>('Connection');
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const initUser = async () => {
    const isLogined = await promiseResolver({
      promise: userRepo.isLogined(),
      errorCallback: (_) => {
        if (location.pathname !== '/clinic/reset' && location.pathname !== '/clinic/signIn')
          navigate('/clinic/signIn', { replace: true });
      },
    });
    if (isLogined) {
      const user = await promiseResolver<User>({
        promise: userRepo.getCurrent(),
        errorCallback: (error: string) => {
          message.error('login failed.');
          connection.setToken('');
        },
      });
      if (user != null) {
        setUser({ ...user, loginState: UserLoginState.logined });
        if (location.pathname === '/clinic/signIn')
          location.pathname = '/clinic/dashboard';
        navigate(location, { replace: true });
      }
    }
  };

  const trySignIn = async (signInInfo: SignInInfo) => {
    setIsLoading(true);
    const token = await promiseResolver<AccessToken>({
      promise: userRepo.signIn(signInInfo),
      isLoadingCallback: setIsLoading,
    });
    if (token != null) {
      connection.setToken(token);
      await initUser();
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  };

  const signOut = () => {
    connection.setToken('');
    navigate(0);
  };

  const requestReset = async (email: Email) => {
    setIsLoading(true);
    await promiseResolver<void>({
      promise: userRepo.requestReset(email),
      isLoadingCallback: setIsLoading,
      successCallback: () => {
        showSuccess(theme.texts.passwordResetEmailSuccess);
      },
      errorCallback: (err) => {
        showError(err);
      },
    });
    setIsLoading(false);
  };

  const tryResetPassword = async (resetInfo: ResetInfo) => {
    setIsLoading(true);
    const validate = await promiseResolver<boolean>({
      promise: userRepo.detectMatch(resetInfo),
      isLoadingCallback: setIsLoading,
    });
    if (validate) {
      await promiseResolver<void>({
        promise: userRepo.resetPassword(resetInfo),
        successCallback: (_) => navigate('/clinic/success', { replace: true }),
      });
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  };

  const tryChangePassword = async (passwordInfo: PasswordInfo) => {
    setIsLoading(true);
    await promiseResolver<void>({
      promise: userRepo.changePassword(passwordInfo),
      successCallback: () => {
        showSuccess(theme.texts.passwordChanged);
        signOut();
      },
      errorCallback: (err) => {
        showError(err);
      },
    });
    setIsLoading(false);
  };

  const validatePassword = async (
    resetInfo: ResetInfo,
    errorCallback?: (error: string) => void
  ) => {
    setIsLoading(true);
    const result = await promiseResolver<boolean>({
      promise: userRepo.detectMatch(resetInfo),
      errorCallback,
    });
    setIsLoading(false);
    return result;
  };

  return {
    user,
    trySignIn,
    initUser,
    isLoading,
    requestReset,
    tryResetPassword,
    tryChangePassword,
    validatePassword,
    signOut,
  };
};
export default useUser;
